import { useState, useEffect, useCallback } from "react";
import { Layers, Sparkles, ArrowRight, Filter, X } from "lucide-react";
import { loadGalleryDesigns, loadPendingGalleryDesigns, approveGalleryDesign, rejectGalleryDesign } from "./supabaseClient";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const C          = "#2D2926";
const ROSE       = "#C9A0A0";
const ROSE_LIGHT = "#F7F0EE";

// ─────────────────────────────────────────────────────────────────────────────
// Inline auth button — mirrors AuthButton in App without needing an import
// ─────────────────────────────────────────────────────────────────────────────
function NavAuthButton({ user, onSignIn, onSignOut }) {
  const [showInput, setShowInput] = useState(false);
  const [email,     setEmail]     = useState("");
  const [sending,   setSending]   = useState(false);
  const [sent,      setSent]      = useState(false);
  const [error,     setError]     = useState(null);
  const [menuOpen,  setMenuOpen]  = useState(false);

  function handleSend() {
    if (!email.trim()) return;
    setSending(true); setError(null);
    onSignIn(email.trim())
      .then(function() { setSent(true); setSending(false); })
      .catch(function(err) { setError(err.message || "Something went wrong."); setSending(false); });
  }

  if (user) {
    const initials = (user.email || "U").slice(0, 2).toUpperCase();
    return (
      <div style={{ position: "relative" }}>
        <button onClick={function() { setMenuOpen(function(o) { return !o; }); }}
          style={{ width: 32, height: 32, borderRadius: "50%", border: "2px solid #C9A0A0", overflow: "hidden", cursor: "pointer", backgroundColor: "#f5f4f2", padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: C, fontFamily: "system-ui,sans-serif" }}>{initials}</span>
        </button>
        {menuOpen && (
          <>
            <div onClick={function() { setMenuOpen(false); }} style={{ position: "fixed", inset: 0, zIndex: 90 }} />
            <div style={{ position: "absolute", right: 0, top: 40, zIndex: 100, backgroundColor: "#fff", borderRadius: 14, boxShadow: "0 8px 32px rgba(0,0,0,0.15)", border: "1px solid #e8e4e0", minWidth: 180, overflow: "hidden" }}>
              <div style={{ padding: "14px 16px", borderBottom: "1px solid #f0eeec" }}>
                <div style={{ fontSize: 10, color: "#a8a29e", fontFamily: "system-ui,sans-serif" }}>{user.email}</div>
              </div>
              <button onClick={function() { setMenuOpen(false); if (onSignOut) onSignOut(); }}
                style={{ display: "block", width: "100%", padding: "12px 16px", textAlign: "left", background: "none", border: "none", fontSize: 11, color: "#c0604a", fontWeight: 600, cursor: "pointer", fontFamily: "system-ui,sans-serif" }}>
                Sign out
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  if (sent) {
    return <div style={{ padding: "7px 12px", borderRadius: 999, border: "1px solid #D8D0C8", backgroundColor: "#fff", fontSize: 9, fontWeight: 700, color: "#7aad7a", letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "system-ui,sans-serif" }}>Check your email ✓</div>;
  }

  if (showInput) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6, position: "relative" }}>
        <input autoFocus type="email" placeholder="your@email.com" value={email}
          onChange={function(e) { setEmail(e.target.value); setError(null); }}
          onKeyDown={function(e) { if (e.key === "Enter") handleSend(); if (e.key === "Escape") { setShowInput(false); setEmail(""); } }}
          style={{ padding: "7px 12px", borderRadius: 999, border: error ? "1px solid #c0604a" : "1px solid #D8D0C8", fontSize: 11, fontFamily: "system-ui,sans-serif", outline: "none", width: 180, color: C }} />
        <button onClick={handleSend} disabled={sending || !email.trim()}
          style={{ padding: "7px 14px", borderRadius: 999, border: "none", backgroundColor: "#C9A0A0", color: C, fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", cursor: sending ? "default" : "pointer", fontFamily: "system-ui,sans-serif", opacity: sending ? 0.6 : 1 }}>
          {sending ? "Sending…" : "Send link"}
        </button>
      </div>
    );
  }

  return (
    <button onClick={function() { setShowInput(true); }}
      style={{ padding: "7px 12px", borderRadius: 999, border: "1px solid #D8D0C8", backgroundColor: "#fff", fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#6b5e58", cursor: "pointer", fontFamily: "system-ui,sans-serif" }}>
      Sign In
    </button>
  );
}

const CATEGORIES = [
  { key: "all",      label: "All" },
  { key: "gala",     label: "Gala" },
  { key: "bridal",   label: "Bridal" },
  { key: "cocktail", label: "Cocktail" },
  { key: "business", label: "Business" },
];

const CATEGORY_LABELS = {
  gala:     "Gala / Black Tie",
  bridal:   "Bridal",
  cocktail: "Cocktail",
  business: "Business",
};

// ─────────────────────────────────────────────────────────────────────────────
// Inline sign-in widget — full email flow, mirrors AuthButton
// ─────────────────────────────────────────────────────────────────────────────
function SignInWidget({ onSignIn }) {
  const [showInput, setShowInput] = useState(false);
  const [email,     setEmail]     = useState("");
  const [sending,   setSending]   = useState(false);
  const [sent,      setSent]      = useState(false);
  const [error,     setError]     = useState(null);

  function handleSend() {
    if (!email.trim()) return;
    setSending(true);
    setError(null);
    onSignIn(email.trim()).then(function() {
      setSent(true);
      setSending(false);
    }).catch(function(err) {
      setError(err.message || "Something went wrong. Please try again.");
      setSending(false);
    });
  }

  if (sent) {
    return (
      <div style={{ padding: "7px 12px", borderRadius: 999, border: "1px solid #D8D0C8", backgroundColor: "#fff", fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#7aad7a", fontFamily: "system-ui, sans-serif" }}>
        Check your email ✓
      </div>
    );
  }

  if (showInput) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6, position: "relative" }}>
        <input
          autoFocus type="email" placeholder="your@email.com" value={email}
          onChange={function(e) { setEmail(e.target.value); setError(null); }}
          onKeyDown={function(e) {
            if (e.key === "Enter") handleSend();
            if (e.key === "Escape") { setShowInput(false); setEmail(""); setError(null); }
          }}
          style={{ padding: "7px 12px", borderRadius: 999, border: error ? "1px solid #c0604a" : "1px solid #D8D0C8", fontSize: 11, fontFamily: "system-ui, sans-serif", outline: "none", width: 180, color: C }}
        />
        <button
          onClick={handleSend} disabled={sending || !email.trim()}
          style={{ padding: "7px 14px", borderRadius: 999, border: "none", backgroundColor: ROSE, color: C, fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", cursor: sending ? "default" : "pointer", fontFamily: "system-ui, sans-serif", opacity: sending ? 0.6 : 1 }}
        >
          {sending ? "Sending…" : "Send link"}
        </button>
        {error && (
          <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, fontSize: 10, color: "#c0604a", fontFamily: "system-ui, sans-serif", whiteSpace: "nowrap" }}>{error}</div>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={function() { setShowInput(true); }}
      style={{ padding: "7px 12px", borderRadius: 999, border: "1px solid #D8D0C8", backgroundColor: "#fff", fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#6b5e58", cursor: "pointer", fontFamily: "system-ui, sans-serif" }}
    >
      Sign In
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function capitalize(s) {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, " ");
}

// ─────────────────────────────────────────────────────────────────────────────
// Filter bar
// ─────────────────────────────────────────────────────────────────────────────
function FilterBar({ active, onChange }) {
  return (
    <div style={{
      display: "flex",
      gap: 8,
      flexWrap: "wrap",
      alignItems: "center",
    }}>
      <Filter style={{ width: 13, height: 13, color: "#a8a29e", flexShrink: 0 }} />
      {CATEGORIES.map(function(cat) {
        const isActive = active === cat.key;
        return (
          <button
            key={cat.key}
            onClick={function() { onChange(cat.key); }}
            style={{
              padding: "5px 14px",
              borderRadius: 999,
              border: isActive ? "none" : "1px solid #D8D0C8",
              backgroundColor: isActive ? C : "#FFFFFF",
              color: isActive ? "#FFFFFF" : C,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              cursor: "pointer",
              fontFamily: "system-ui, sans-serif",
              transition: "all 0.15s ease",
            }}
          >
            {cat.label}
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Gallery card
// ─────────────────────────────────────────────────────────────────────────────
function GalleryCard({ item, onStartWith }) {
  const [hovered, setHovered] = useState(false);

  const catLabel = CATEGORY_LABELS[item.form.category] || capitalize(item.form.category);
  const fabric   = capitalize(item.form.fabricId);
  const cut      = item.form.cut || "A-line";

  return (
    <div
      onMouseEnter={function() { setHovered(true); }}
      onMouseLeave={function() { setHovered(false); }}
      style={{
        borderRadius: 16,
        overflow: "hidden",
        backgroundColor: "#FFFFFF",
        border: "1px solid #E8E2DC",
        boxShadow: hovered
          ? "0 12px 40px rgba(45,41,38,0.13)"
          : "0 2px 10px rgba(45,41,38,0.06)",
        transition: "box-shadow 0.25s ease, transform 0.25s ease",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        display: "flex",
        flexDirection: "column",
        cursor: "default",
      }}
    >
      {/* Image area */}
      <div style={{
        position: "relative",
        backgroundColor: "#1a1714",
        aspectRatio: "3 / 4",
        overflow: "hidden",
      }}>
        {item.designRender ? (
          <img
            src={item.designRender}
            alt={catLabel + " design"}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "top center",
              display: "block",
              transition: "transform 0.4s ease",
              transform: hovered ? "scale(1.04)" : "scale(1)",
            }}
          />
        ) : (
          <div style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "rgba(255,255,255,0.18)",
            fontSize: 10,
            letterSpacing: "0.2em",
            fontFamily: "system-ui, sans-serif",
          }}>
            No Preview
          </div>
        )}

        {/* Category pill */}
        <div style={{
          position: "absolute",
          top: 12,
          left: 12,
          backgroundColor: "rgba(26,23,20,0.68)",
          backdropFilter: "blur(6px)",
          padding: "3px 10px",
          borderRadius: 999,
          fontSize: 8,
          fontWeight: 700,
          letterSpacing: "0.18em",
          color: "rgba(255,255,255,0.9)",
          fontFamily: "system-ui, sans-serif",
          textTransform: "uppercase",
        }}>
          {catLabel}
        </div>

        {/* Design seed */}
        {item.designSeed && (
          <div style={{
            position: "absolute",
            bottom: 10,
            right: 10,
            fontSize: 8,
            fontWeight: 400,
            color: "rgba(255,255,255,0.5)",
            fontFamily: "system-ui, sans-serif",
            letterSpacing: "0.08em",
          }}>
            No. {item.designSeed}
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: "16px 16px 14px" }}>
        {/* Colour swatch + name */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <div style={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            backgroundColor: item.form.colorHex || ROSE,
            border: "1px solid rgba(0,0,0,0.08)",
            flexShrink: 0,
          }} />
          <span style={{
            fontSize: 10,
            fontWeight: 600,
            color: C,
            fontFamily: "system-ui, sans-serif",
            letterSpacing: "0.02em",
          }}>
            {item.form.colorDescription || "—"} {fabric}
          </span>
        </div>

        {/* Cut + silhouette tags */}
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 14 }}>
          {[cut, item.form.neckline, item.form.sleeves, item.form.dressLength].filter(Boolean).map(function(tag, i) {
            return (
              <span
                key={i}
                style={{
                  backgroundColor: ROSE_LIGHT,
                  color: "#7a5b5b",
                  fontSize: 8,
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  padding: "2px 8px",
                  borderRadius: 4,
                  fontFamily: "system-ui, sans-serif",
                }}
              >
                {tag}
              </span>
            );
          })}
        </div>

        {/* Atmosphere excerpt */}
        {item.form.atmosphere && (
          <p style={{
            fontSize: 9,
            color: "#9c948d",
            lineHeight: 1.55,
            fontStyle: "italic",
            margin: "0 0 14px",
            fontFamily: "system-ui, sans-serif",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}>
            "{item.form.atmosphere}"
          </p>
        )}

        {/* CTA */}
        <button
          onClick={function() { onStartWith(item); }}
          style={{
            width: "100%",
            padding: "10px 0",
            borderRadius: 10,
            border: "none",
            backgroundColor: C,
            color: "#FFFFFF",
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            cursor: "pointer",
            fontFamily: "system-ui, sans-serif",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 7,
            transition: "background-color 0.15s ease",
          }}
          onMouseEnter={function(e) { e.currentTarget.style.backgroundColor = ROSE; }}
          onMouseLeave={function(e) { e.currentTarget.style.backgroundColor = C; }}
        >
          <Sparkles style={{ width: 10, height: 10 }} />
          Start with this design
          <ArrowRight style={{ width: 10, height: 10 }} />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Empty state
// ─────────────────────────────────────────────────────────────────────────────
function EmptyState({ category }) {
  return (
    <div style={{
      gridColumn: "1 / -1",
      textAlign: "center",
      padding: "64px 24px",
      color: "#a8a29e",
      fontFamily: "system-ui, sans-serif",
    }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>✦</div>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 8 }}>
        No {category !== "all" ? CATEGORY_LABELS[category] || category : ""} designs yet
      </div>
      <div style={{ fontSize: 10, color: "#c4bdb7" }}>
        New templates are added regularly — check back soon.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin review panel — shown inside Gallery when isAdmin is true
// ─────────────────────────────────────────────────────────────────────────────
function AdminReviewPanel({ onRefreshGallery }) {
  const [pending,   setPending]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [actioning, setActioning] = useState({}); // { [id]: 'approving'|'rejecting' }

  const fetchPending = useCallback(async function() {
    try {
      setLoading(true);
      const rows = await loadPendingGalleryDesigns();
      setPending(rows);
    } catch (err) {
      console.error("Failed to load pending designs:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(function() { fetchPending(); }, [fetchPending]);

  async function handleApprove(item) {
    setActioning(function(a) { return { ...a, [item.id]: 'approving' }; });
    try {
      await approveGalleryDesign(item.id, item.designRender, item.designSeed);
      setPending(function(p) { return p.filter(function(d) { return d.id !== item.id; }); });
      if (onRefreshGallery) onRefreshGallery();
    } catch (err) {
      console.error("Approve failed:", err);
    } finally {
      setActioning(function(a) { const n = {...a}; delete n[item.id]; return n; });
    }
  }

  async function handleReject(item) {
    setActioning(function(a) { return { ...a, [item.id]: 'rejecting' }; });
    try {
      await rejectGalleryDesign(item.id);
      setPending(function(p) { return p.filter(function(d) { return d.id !== item.id; }); });
    } catch (err) {
      console.error("Reject failed:", err);
    } finally {
      setActioning(function(a) { const n = {...a}; delete n[item.id]; return n; });
    }
  }

  return (
    <div style={{ margin:'0 0 40px', padding:'28px 28px 0' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <div>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:'0.22em', textTransform:'uppercase', color:'#6f555f', fontFamily:'system-ui,sans-serif' }}>
            Admin — Pending Submissions
          </div>
          <div style={{ fontSize:12, color:'#78716c', marginTop:2, fontFamily:'system-ui,sans-serif' }}>
            {loading ? 'Loading…' : pending.length === 0 ? 'No submissions awaiting review.' : `${pending.length} design${pending.length !== 1 ? 's' : ''} awaiting review`}
          </div>
        </div>
        <button
          onClick={fetchPending}
          style={{ padding:'7px 14px', borderRadius:8, border:'1px solid #e6dce2', backgroundColor:'#fff', fontSize:9, fontWeight:700, letterSpacing:'0.15em', textTransform:'uppercase', color:'#78716c', cursor:'pointer', fontFamily:'system-ui,sans-serif' }}
        >
          Refresh
        </button>
      </div>

      {!loading && pending.length > 0 && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px, 1fr))', gap:16 }}>
          {pending.map(function(item) {
            const busy = actioning[item.id];
            return (
              <div key={item.id} style={{ backgroundColor:'#fff', borderRadius:16, overflow:'hidden', border:'1px solid #e6dce2', boxShadow:'0 2px 12px rgba(0,0,0,0.05)' }}>
                {item.designRender ? (
                  <img src={item.designRender} alt="Pending design" style={{ width:'100%', aspectRatio:'3/4', objectFit:'cover', display:'block' }} />
                ) : (
                  <div style={{ width:'100%', aspectRatio:'3/4', backgroundColor:'#f5f5f4', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <span style={{ fontSize:10, color:'#b8b0a8', fontFamily:'system-ui,sans-serif' }}>No render</span>
                  </div>
                )}
                <div style={{ padding:'14px 14px 16px' }}>
                  <div style={{ fontSize:10, fontWeight:700, color:'#2D2926', marginBottom:4, fontFamily:'system-ui,sans-serif' }}>
                    Design No. {item.designSeed || '—'}
                  </div>
                  <div style={{ fontSize:10, color:'#78716c', marginBottom:12, fontFamily:'system-ui,sans-serif', lineHeight:1.5 }}>
                    {[item.form?.category, item.form?.colorDescription, item.form?.fabricId].filter(Boolean).join(' · ')}
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    <button
                      disabled={!!busy}
                      onClick={function() { handleApprove(item); }}
                      style={{ padding:'9px 0', borderRadius:10, border:'none', backgroundColor: busy === 'approving' ? '#e7e5e4' : '#2a6e42', color:'#fff', fontSize:9, fontWeight:700, letterSpacing:'0.15em', textTransform:'uppercase', cursor: busy ? 'default' : 'pointer', fontFamily:'system-ui,sans-serif' }}
                    >
                      {busy === 'approving' ? '…' : '✓ Approve'}
                    </button>
                    <button
                      disabled={!!busy}
                      onClick={function() { handleReject(item); }}
                      style={{ padding:'9px 0', borderRadius:10, border:'1px solid #e6dce2', backgroundColor:'#fdf3f3', color:'#b04040', fontSize:9, fontWeight:700, letterSpacing:'0.15em', textTransform:'uppercase', cursor: busy ? 'default' : 'pointer', fontFamily:'system-ui,sans-serif' }}
                    >
                      {busy === 'rejecting' ? '…' : '✕ Reject'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ borderBottom:'1px solid #e6dce2', marginTop:28 }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Gallery component
// ─────────────────────────────────────────────────────────────────────────────
export default function TheGallery({
  onStartWithDesign,
  onNewDesign,
  onNavigate,
  user,
  onSignIn,
  onSignOut,
  isAdmin = false,
}) {
  const [designs,    setDesigns]   = useState([]);
  const [loading,    setLoading]   = useState(true);
  const [error,      setError]     = useState(null);
  const [filter,     setFilter]    = useState("all");


  // ── Fetch gallery designs ────────────────────────────────────────────────────
  const fetchDesigns = useCallback(async function() {
    try {
      setLoading(true);
      setError(null);
      const rows = await loadGalleryDesigns();
      setDesigns(rows);
    } catch (err) {
      console.error("Gallery load failed:", err);
      setError("Could not load the gallery — please refresh.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(function() {
    fetchDesigns();
  }, [fetchDesigns]);

  // ── Filter ───────────────────────────────────────────────────────────────────
  const visible = filter === "all"
    ? designs
    : designs.filter(function(d) { return d.form.category === filter; });

  // ── Handle "Start with this design" ─────────────────────────────────────────
  function handleStartWith(item) {
    if (onStartWithDesign) {
      onStartWithDesign({
        form:         item.form,
        designRender: item.designRender,
        designSeed:   item.designSeed,
        // gallery forks always start fresh — not saved, not locked
        isSaved:      false,
        isLocked:     false,
      });
    }
  }

  return (
    <div style={{
      margin: 0,
      padding: 0,
      minHeight: "100vh",
      backgroundColor: "#F7F4F1",
      fontFamily: "system-ui, sans-serif",
    }}>

      {/* ── Studio-style sticky dusty rose header ───────────────────────────── */}
      <div style={{ margin:0, padding:"18px 28px", backgroundColor:ROSE, borderBottom:"1px solid #b88f8f", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:60 }}>
        <button onClick={function() { if (onNavigate) onNavigate("step1"); }} style={{ display:"flex", alignItems:"center", gap:12, background:"none", border:"none", padding:0, cursor:"pointer" }}>
          <Layers style={{ color:C, width:24, height:24, flexShrink:0 }} />
          <div style={{ textAlign:"left" }}>
            <div style={{ margin:0, padding:0, fontSize:21, fontWeight:700, letterSpacing:"-0.02em", color:C, fontFamily:"system-ui,sans-serif" }}>Tabitha</div>
            <div style={{ margin:0, padding:0, fontSize:10, fontWeight:700, letterSpacing:"0.24em", color:"#6f555f", textTransform:"uppercase", fontFamily:"system-ui,sans-serif" }}>Gallery</div>
          </div>
        </button>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <button style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"default", fontFamily:"system-ui,sans-serif", opacity:0.45 }}>
            Gallery
          </button>
          <button onClick={onNewDesign} style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>
            New Design
          </button>
          <NavAuthButton user={user} onSignIn={onSignIn} onSignOut={onSignOut} />
          <button onClick={function() { if (onNavigate) onNavigate("collection"); }} style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>
            My Collection
          </button>
          <button onClick={function() { if (onNavigate) onNavigate("contact"); }} style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>
            Contact Us
          </button>
        </div>
      </div>

      {/* ── Filter + grid ───────────────────────────────────────────────────── */}
      <div style={{ maxWidth: 960, margin: "0 auto", padding: "28px 20px 80px" }}>

        {/* Admin review panel — only visible to admins */}
        {isAdmin && <AdminReviewPanel onRefreshGallery={fetchDesigns} />}

        {/* Filter bar */}
        <div style={{ marginBottom: 24 }}>
          <FilterBar active={filter} onChange={setFilter} />
        </div>

        {/* Loading state */}
        {loading && (
          <div style={{
            textAlign: "center",
            padding: "64px 24px",
            color: "#a8a29e",
            fontSize: 10,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            fontFamily: "system-ui, sans-serif",
          }}>
            <div style={{
              width: 28,
              height: 28,
              border: "2px solid #E8E2DC",
              borderTopColor: ROSE,
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              margin: "0 auto 16px",
            }} />
            Loading gallery…
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div style={{
            textAlign: "center",
            padding: "48px 24px",
            color: "#c0604a",
            fontFamily: "system-ui, sans-serif",
          }}>
            <div style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 12 }}>
              {error}
            </div>
            <button
              onClick={fetchDesigns}
              style={{
                padding: "8px 20px",
                borderRadius: 999,
                border: "1px solid #D8D0C8",
                backgroundColor: "#FFFFFF",
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                cursor: "pointer",
                color: C,
                fontFamily: "system-ui, sans-serif",
              }}
            >
              Try again
            </button>
          </div>
        )}

        {/* Grid */}
        {!loading && !error && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: 20,
          }}>
            {visible.length === 0 ? (
              <EmptyState category={filter} />
            ) : (
              visible.map(function(item) {
                return (
                  <GalleryCard
                    key={item.id}
                    item={item}
                    onStartWith={handleStartWith}
                  />
                );
              })
            )}
          </div>
        )}

        {/* Count */}
        {!loading && !error && visible.length > 0 && (
          <div style={{
            textAlign: "center",
            marginTop: 40,
            fontSize: 9,
            color: "#c4bdb7",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            fontFamily: "system-ui, sans-serif",
          }}>
            {visible.length} {visible.length === 1 ? "design" : "designs"}
            {filter !== "all" && " in " + (CATEGORY_LABELS[filter] || filter)}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        textAlign: "center",
        padding: "24px 0 32px",
        fontSize: 9,
        color: "#b8b0a8",
        letterSpacing: "0.15em",
        fontFamily: "system-ui, sans-serif",
      }}>
        © Tabitha 2026 · <a href="/privacy.html" style={{ color: "#b8b0a8", textDecoration: "underline" }}>Privacy Policy</a> · <a href="/terms.html" style={{ color: "#b8b0a8", textDecoration: "underline" }}>Terms &amp; Conditions</a> · <a href="/faq.html" style={{ color: "#b8b0a8", textDecoration: "underline" }}>FAQ</a>
      </div>
    </div>
  );
}
